/* eslint-disable react/jsx-props-no-spreading */
// Tooltip using Floating UI - backward compatible with original API
import clsx from 'clsx';
import React, { type ReactNode, useState } from 'react';
import { type Config } from 'react-popper-tooltip';
import {
  type Placement,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import type {
  TWHeightProp,
  TWPaddingProp,
  TWWidthProp,
} from '../../types/tailwind';
import { useSafeFloating } from './FloatingUIHelpers';

// Convert react-popper-tooltip placement to Floating UI placement
const convertPlacement = (placement?: Config['placement']): Placement => {
  if (!placement) {
    return 'top';
  }
  switch (placement) {
    case 'auto':
      return 'top';
    case 'auto-start':
      return 'top-start';
    case 'auto-end':
      return 'top-end';
    default:
      return placement as Placement;
  }
};

// Keep backward compatibility with original API from dev
type TooltipProps<WProp, HProp, PaddingProp> = {
  baseComponent: ReactNode;
  tooltipComponent: ReactNode;
  trigger?: Config['trigger'];
  placement?: Config['placement'];
  offset?: [number, number];
  delayShow?: number;
  delayHide?: number;
  width?: TWWidthProp<WProp>;
  height?: TWHeightProp<HProp>;
  tooltipDisabled?: boolean;
  onVisibleChange?: (state: boolean) => void;
  padding?: TWPaddingProp<PaddingProp>;
};

/**
 * Tooltip using Floating UI for automatic clipping prevention
 * Backward compatible with original API from dev branch
 */
export const Tooltip = <WProp, HProp, PaddingProp>({
  baseComponent,
  tooltipComponent,
  trigger = 'hover',
  placement = 'top-start',
  offset: offsetValue = [0, 0],
  delayShow = 500,
  delayHide = 0,
  padding = 'p-0' as TWPaddingProp<PaddingProp>,
  tooltipDisabled = false,
  onVisibleChange,
  width,
  height,
}: TooltipProps<WProp, HProp, PaddingProp>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  // useSafeFloating enforces open + onOpenChange at compile time (demonstrates best practice)
  const { refs, floatingStyles, context } = useSafeFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      onVisibleChange?.(open);
    },
    placement: convertPlacement(placement),
    middleware: [
      offset({ mainAxis: offsetValue[1], crossAxis: offsetValue[0] }),
      flip(),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // Configure interactions based on trigger type
  const triggerArray = Array.isArray(trigger) ? trigger : [trigger];

  // Always call hooks unconditionally (Rules of Hooks)
  const hover = useHover(context, {
    delay: { open: delayShow, close: delayHide },
    enabled: triggerArray.includes('hover'),
  });
  const focus = useFocus(context, {
    enabled: triggerArray.includes('focus'),
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  // Include all interactions (enabled/disabled handled by hooks)
  const interactions = [hover, focus, dismiss, role];

  const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

  return (
    <>
      <div
        className={clsx([width, height])}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {baseComponent}
      </div>
      {isOpen && !tooltipDisabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1000 }}
            {...getFloatingProps()}
            className={clsx(
              'tooltip-container',
              'border-canvas border-[0.5px]',
              'flex items-center',
              'bg-negative text-main',
              padding
            )}
          >
            {tooltipComponent}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
